import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

// Color map for SIP methods / status codes
const getColor = (method, statusCode) => {
  if (statusCode) {
    const code = parseInt(statusCode);
    if (code >= 100 && code < 200) return "#9e9e9e"; // 1xx gray
    if (code >= 200 && code < 300) return "#4caf50"; // 2xx green
    if (code >= 300 && code < 400) return "#ff9800"; // 3xx orange
    if (code >= 400 && code < 500) return "#f44336"; // 4xx red
    if (code >= 500) return "#d32f2f"; // 5xx dark red
  }
  const colors = {
    INVITE: "#2196f3",
    ACK: "#607d8b",
    BYE: "#ff9800",
    CANCEL: "#ff5722",
    REGISTER: "#9c27b0",
    OPTIONS: "#607d8b",
    PRACK: "#00bcd4",
    UPDATE: "#00bcd4",
    INFO: "#607d8b",
    REFER: "#673ab7",
    NOTIFY: "#795548",
    SUBSCRIBE: "#795548",
  };
  return colors[method] || "#607d8b";
};

const ENDPOINT_WIDTH = 220;
const ROW_HEIGHT = 45;
const HEADER_HEIGHT = 70;
const LEFT_MARGIN = 100;
const ARROW_HEAD_SIZE = 8;

const SipTraceLadderDiagram = ({
  messages,
  selectedCallId,
  onSelectCallId,
  onViewMessage,
  isDark = false,
  serverIp = "",
}) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  // Center endpoint key. Defaults to the literal "Asterisk" so the diagram
  // still renders when PORTAL_SERVER_IP env is unset on the backend.
  const centerIp = serverIp || "Asterisk";

  // Theme-aware tokens — the scoped CSS in style.css already overrides the Paper
  // bg/border color via !important, but SVG text fills are drawn on canvas and
  // don't inherit — they must be set explicitly per theme to stay legible.
  const paperBg = isDark ? "#1a2332" : "#fafafa";
  const paperBorder = isDark ? "#2a3a4a" : "#e0e0e0";
  const lifelineStroke = isDark ? "#3a4a5a" : "#ccc";
  const timestampFill = isDark ? "#8899aa" : "#757575";
  const tooltipFill = isDark ? "#a0b4c8" : "#9e9e9e";
  const endpointSubFill = isDark ? "#cfd8dc" : "#cfd8dc"; // sits on dark rect — same in both
  const headerStroke = isDark ? "#3a4a5a" : "#ccc";
  const hoverFill = isDark ? "rgba(66, 165, 245, 0.08)" : "rgba(25, 118, 210, 0.05)";

  // Extract unique Call-IDs from messages for the dropdown
  const callIds = useMemo(() => {
    const ids = new Set();
    messages.forEach((m) => {
      if (m.call_id) ids.add(m.call_id);
    });
    return Array.from(ids);
  }, [messages]);

  // Filter messages for selected Call-ID (or show all if none selected)
  const filteredMessages = useMemo(() => {
    if (!selectedCallId) return messages;
    return messages.filter((m) => m.call_id === selectedCallId);
  }, [messages, selectedCallId]);

  // Extract unique endpoints (IPs) and determine their positions
  const endpoints = useMemo(() => {
    const epMap = new Map();

    filteredMessages.forEach((msg) => {
      const remoteKey = msg.remote_ip;
      if (remoteKey && !epMap.has(remoteKey)) {
        epMap.set(remoteKey, {
          ip: msg.remote_ip,
          port: msg.remote_port,
          carrier: msg.carrier || "",
          label: msg.carrier || msg.remote_ip,
        });
      }
    });

    // Center endpoint = the portal server. Use the configured serverIp as
    // both the endpoint key (for arrow routing) and the subtitle. Label is
    // the operator-facing "Destination IP" string.
    const asteriskIp = centerIp;
    const eps = [];

    // Left side: incoming endpoints (received messages)
    const leftEps = [];
    const rightEps = [];

    epMap.forEach((ep, key) => {
      // Check if this IP appears as received (caller side) or transmitting (carrier side)
      const receivedFrom = filteredMessages.some(
        (m) => m.direction === "received" && m.remote_ip === key
      );
      const sentTo = filteredMessages.some(
        (m) => m.direction === "transmitting" && m.remote_ip === key
      );

      if (receivedFrom && !sentTo) {
        leftEps.push(ep);
      } else if (sentTo && !receivedFrom) {
        rightEps.push(ep);
      } else {
        // Appears on both sides — put on left
        leftEps.push(ep);
      }
    });

    leftEps.forEach((ep) => eps.push(ep));
    eps.push({ ip: asteriskIp, port: "", carrier: "", label: "Destination IP" });
    rightEps.forEach((ep) => eps.push(ep));

    // If no distinct endpoints, just show Asterisk
    if (eps.length === 1 && epMap.size > 0) {
      epMap.forEach((ep) => eps.push(ep));
    }

    return eps;
  }, [filteredMessages, centerIp]);

  // Map endpoint IP to X position
  const epPositions = useMemo(() => {
    const pos = {};
    endpoints.forEach((ep, i) => {
      pos[ep.ip] = LEFT_MARGIN + i * ENDPOINT_WIDTH + ENDPOINT_WIDTH / 2;
    });
    return pos;
  }, [endpoints]);

  const svgWidth = Math.max(LEFT_MARGIN + endpoints.length * ENDPOINT_WIDTH + 40, 600);
  const svgHeight = HEADER_HEIGHT + filteredMessages.length * ROW_HEIGHT + 40;

  if (filteredMessages.length === 0) {
    return (
      <Box>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Select Call-ID</InputLabel>
            <Select
              value={selectedCallId || ""}
              label="Select Call-ID"
              onChange={(e) => onSelectCallId(e.target.value || null)}
            >
              <MenuItem value="">All calls</MenuItem>
              {callIds.map((cid) => (
                <MenuItem key={cid} value={cid}>
                  {cid.length > 50 ? cid.substring(0, 50) + "..." : cid}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {messages.length === 0
              ? "No messages captured yet. Start a capture or select a Call-ID."
              : "Select a Call-ID from the dropdown to view the ladder diagram."}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Call-ID selector */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Select Call-ID</InputLabel>
          <Select
            value={selectedCallId || ""}
            label="Select Call-ID"
            onChange={(e) => onSelectCallId(e.target.value || null)}
          >
            <MenuItem value="">All calls</MenuItem>
            {callIds.map((cid) => (
              <MenuItem key={cid} value={cid}>
                {cid.length > 50 ? cid.substring(0, 50) + "..." : cid}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {filteredMessages.length} messages
          {selectedCallId ? ` for Call-ID: ${selectedCallId.substring(0, 30)}...` : ""}
        </Typography>
      </Stack>

      {/* SVG Ladder Diagram */}
      <Paper
        sx={{
          overflow: "auto",
          maxHeight: "70vh",
          bgcolor: paperBg,
          border: `1px solid ${paperBorder}`,
        }}
      >
        <svg width={svgWidth} height={svgHeight} style={{ display: "block" }}>
          <defs>
            {/* Arrowhead markers for each color */}
            {[
              "#2196f3", "#4caf50", "#f44336", "#d32f2f", "#ff9800",
              "#ff5722", "#9e9e9e", "#607d8b", "#9c27b0", "#00bcd4",
              "#673ab7", "#795548",
            ].map((color) => (
              <marker
                key={color}
                id={`arrow-${color.replace("#", "")}`}
                markerWidth={ARROW_HEAD_SIZE}
                markerHeight={ARROW_HEAD_SIZE}
                refX={ARROW_HEAD_SIZE - 1}
                refY={ARROW_HEAD_SIZE / 2}
                orient="auto"
              >
                <polygon
                  points={`0 0, ${ARROW_HEAD_SIZE} ${ARROW_HEAD_SIZE / 2}, 0 ${ARROW_HEAD_SIZE}`}
                  fill={color}
                />
              </marker>
            ))}
          </defs>

          {/* Endpoint headers */}
          {endpoints.map((ep, i) => {
            const x = LEFT_MARGIN + i * ENDPOINT_WIDTH + ENDPOINT_WIDTH / 2;
            return (
              <g key={ep.ip}>
                {/* Header box */}
                <rect
                  x={x - 70}
                  y={10}
                  width={140}
                  height={45}
                  rx={6}
                  fill={ep.ip === centerIp ? "#1976d2" : "#455a64"}
                  stroke={headerStroke}
                />
                <text
                  x={x}
                  y={28}
                  textAnchor="middle"
                  fill="white"
                  fontSize={11}
                  fontWeight="bold"
                >
                  {ep.label.length > 18 ? ep.label.substring(0, 18) + "..." : ep.label}
                </text>
                <text x={x} y={44} textAnchor="middle" fill={endpointSubFill} fontSize={9}>
                  {ep.ip === centerIp ? (serverIp || "PBX") : `${ep.ip}:${ep.port}`}
                </text>

                {/* Vertical lifeline */}
                <line
                  x1={x}
                  y1={HEADER_HEIGHT}
                  x2={x}
                  y2={svgHeight - 10}
                  stroke={lifelineStroke}
                  strokeDasharray="4,4"
                  strokeWidth={1}
                />
              </g>
            );
          })}

          {/* Message arrows */}
          {filteredMessages.map((msg, i) => {
            const y = HEADER_HEIGHT + i * ROW_HEIGHT + ROW_HEIGHT / 2;
            const asteriskX = epPositions[centerIp];
            const remoteX = epPositions[msg.remote_ip];

            if (!asteriskX || !remoteX) return null;

            // Determine arrow direction
            let fromX, toX;
            if (msg.direction === "received") {
              fromX = remoteX;
              toX = asteriskX;
            } else {
              fromX = asteriskX;
              toX = remoteX;
            }

            const color = getColor(msg.method, msg.status_code);
            const markerId = `arrow-${color.replace("#", "")}`;
            const label = msg.status_code
              ? `${msg.status_code} ${msg.status_text || ""}`
              : msg.method || "";
            const isHovered = hoveredIdx === i;

            return (
              <g
                key={msg.id || i}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={() => onViewMessage(msg.id)}
                style={{ cursor: "pointer" }}
              >
                {/* Hover background */}
                {isHovered && (
                  <rect
                    x={0}
                    y={y - ROW_HEIGHT / 2 + 2}
                    width={svgWidth}
                    height={ROW_HEIGHT - 4}
                    fill={hoverFill}
                    rx={2}
                  />
                )}

                {/* Timestamp */}
                <text
                  x={8}
                  y={y + 4}
                  fontSize={10}
                  fill={timestampFill}
                  fontFamily="monospace"
                >
                  {msg.timestamp ? dayjs(msg.timestamp).format("HH:mm:ss.SSS") : ""}
                </text>

                {/* Arrow line */}
                <line
                  x1={fromX}
                  y1={y}
                  x2={toX - (toX > fromX ? 12 : -12)}
                  y2={y}
                  stroke={color}
                  strokeWidth={isHovered ? 2.5 : 1.5}
                  markerEnd={`url(#${markerId})`}
                />

                {/* Label on arrow */}
                <text
                  x={(fromX + toX) / 2}
                  y={y - 6}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight={isHovered ? "bold" : "normal"}
                  fill={color}
                >
                  {label.length > 25 ? label.substring(0, 25) + "..." : label}
                </text>

                {/* Tooltip info on hover */}
                {isHovered && (
                  <text
                    x={(fromX + toX) / 2}
                    y={y + 14}
                    textAnchor="middle"
                    fontSize={9}
                    fill={tooltipFill}
                  >
                    {msg.from_user || ""} → {msg.to_user || ""}{" "}
                    {msg.user_agent ? `[${msg.user_agent}]` : ""}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </Paper>
    </Box>
  );
};

export default SipTraceLadderDiagram;
